import React, { useMemo, useEffect } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Unstable_Grid2';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { getDetailsTotal, PREDICTION_TOTAL_MODES } from '../../../components/utils/expenseDetailsUtils';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);

const ExpenseDetailsForm = ({
  state,
  updateField,
  onAmountBlur,
  errors,
  setErrors,
  expenseTotalAmount,
  isPrediction,
  totalMode,
  onTotalModeChange,
  lockedDetailIds = new Set(),
  onToggleLock,
}) => {
  const calculatedTotal = getDetailsTotal(state?.expense_details);

  const sortedDetails = useMemo(() => {
    return [...(state.expense_details || [])].sort((a, b) => a.exp_d_episode - b.exp_d_episode);
  }, [state.expense_details]);

  const isFixedMode = isPrediction && totalMode === PREDICTION_TOTAL_MODES.FIXED;
  const isVariableMode = isPrediction && totalMode === PREDICTION_TOTAL_MODES.VARIABLE;

  const isTotalValid =
    expenseTotalAmount !== null &&
    calculatedTotal.toFixed(2) === expenseTotalAmount.toFixed(2);

  useEffect(() => {
    if (isPrediction) {
      if (isFixedMode && !isTotalValid && calculatedTotal > 0) {
        setErrors({
          total: `Το άθροισμα υπερβαίνει το σύνολο της πρόβλεψης (${formatCurrency(expenseTotalAmount)}).`,
        });
      } else {
        setErrors({});
      }
      return;
    }

    if (!isTotalValid) {
      setErrors({
        total: `Το συνολικό ποσό δεν ταιριάζει με το αναμενόμενο ποσό των ${formatCurrency(expenseTotalAmount)}.`,
      });
    } else {
      setErrors({});
    }
  }, [
    calculatedTotal,
    expenseTotalAmount,
    isPrediction,
    isFixedMode,
    isTotalValid,
    setErrors,
  ]);

  return (
    <Box p={4} display="flex" flexDirection="column" alignItems="center" width="100%">
      {isPrediction && (
        <FormControl sx={{ mb: 3, width: '100%' }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Συμπεριφορά συνόλου πρόβλεψης
          </Typography>
          <RadioGroup
            row
            value={totalMode}
            onChange={(e) => onTotalModeChange(e.target.value)}
          >
            <FormControlLabel
              value={PREDICTION_TOTAL_MODES.FIXED}
              control={<Radio size="small" />}
              label="Σταθερό σύνολο"
            />
            <FormControlLabel
              value={PREDICTION_TOTAL_MODES.VARIABLE}
              control={<Radio size="small" />}
              label="Μεταβλητό σύνολο"
            />
          </RadioGroup>
          <Typography variant="caption" color="text.secondary">
            {isFixedMode
              ? 'Το σύνολο παραμένει σταθερό — η διαφορά κατανέμεται αυτόματα στα ξεκλείδωτα επεισόδια. Κλειδώστε επεισόδια που δεν θέλετε να αλλάξουν.'
              : 'Το σύνολο μπορεί να αλλάξει — κατά την αποθήκευση θα ζητηθεί επιβεβαίωση.'}
          </Typography>
        </FormControl>
      )}

      <Box mt={1} display="flex" flexDirection="column" alignItems="center">
        <Typography variant="h4">Σύνολο: {formatCurrency(calculatedTotal)}</Typography>
        {isPrediction && isFixedMode && (
          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 0.5 }}>
            Στόχος: {formatCurrency(expenseTotalAmount)}
          </Typography>
        )}
        {isTotalValid ? (
          <Typography variant="subtitle1" color="success.main" sx={{ mt: 1 }}>
            Το συνολικό ποσό ταιριάζει με το αναμενόμενο ποσό!
          </Typography>
        ) : isVariableMode ? (
          <Typography
            variant="subtitle1"
            color="warning.main"
            sx={{ textAlign: 'center', mt: 1 }}
          >
            Το σύνολο άλλαξε από {formatCurrency(expenseTotalAmount)} σε{' '}
            {formatCurrency(calculatedTotal)}. Κατά την αποθήκευση θα σας ζητηθεί
            επιβεβαίωση ενημέρωσης της πρόβλεψης.
          </Typography>
        ) : errors.total ? (
          <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
            {errors.total}
          </Typography>
        ) : null}
      </Box>

      <Grid container spacing={1} sx={{ width: '100%', mt: 2 }}>
        {state.expense_details?.length > 0 ? (
          sortedDetails.map((expenseDetail) => {
            const isLocked = lockedDetailIds.has(expenseDetail.id);
            return (
              <Grid key={expenseDetail.id} item xs={12} sm={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <TextField
                    fullWidth
                    label={`Επεισόδιο ${expenseDetail.exp_d_episode}`}
                    value={expenseDetail.exp_d_amount ?? ''}
                    disabled={isLocked}
                    onChange={(e) =>
                      updateField(expenseDetail.id, 'exp_d_amount', e.target.value)
                    }
                    onBlur={(e) =>
                      onAmountBlur?.(expenseDetail.id, e.target.value)
                    }
                    error={!!errors.exp_d_amount}
                    helperText={errors.exp_d_amount}
                    sx={
                      isLocked
                        ? {
                            '& .MuiInputBase-input.Mui-disabled': {
                              WebkitTextFillColor: 'text.primary',
                              opacity: 0.7,
                            },
                          }
                        : undefined
                    }
                  />
                  {onToggleLock && (
                    <Tooltip title={isLocked ? 'Ξεκλείδωμα επεισοδίου' : 'Κλείδωμα επεισοδίου'}>
                      <IconButton
                        onClick={() => onToggleLock(expenseDetail.id)}
                        aria-label={isLocked ? 'Ξεκλείδωμα' : 'Κλείδωμα'}
                        sx={{ color: isLocked ? '#ff9800' : 'action.disabled' }}
                      >
                        {isLocked ? <LockIcon /> : <LockOpenIcon />}
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Grid>
            );
          })
        ) : (
          <Typography variant="body2" color="textSecondary">
            Δεν υπάρχουν διαθέσιμα στοιχεία εξόδων για επεξεργασία.
          </Typography>
        )}
      </Grid>
    </Box>
  );
};

export default ExpenseDetailsForm;
